/** Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/

import type { AxiosInstance } from 'axios';

import { Service } from '@hems/service/src/api/Service';
import { apiWrapper } from '@hems/service/src/util/apiHelper';

import type { CommonListQueryParamNew } from 'hems/common/dataGrid';
import type { EmptyObject, EmptyResponse } from 'hems/energyManagement';
import type { FirmwareReleaseDataResponse } from 'hems/firmwareDownload';
import type {
  CreateFirmwareGroupRequestBody,
  CreateFirmwareRequestBody,
  CreateFirmwareSetRequestBody,
  FirmwareUpdateCreateStep2DeviceResponse,
  FirmwareUpdateDeviceResultResponse,
  FirmwareUpdateGroupResponse,
  FirmwareUpdateListParams,
  FirmwareUpdatePostAPIResponse,
  FirmwareUpdatePresignedURLResponse,
  FirmwareUpdateValidateConfigurationParams,
  FirmwareUpdateValidateGroupNameParams,
  FirmwareUpdateValidateResponse,
  GetFirmwarePresignedURLParams,
  pushFirmwareUpdateExcludeSiteIdsRequestBody,
  PushFirmwareUpdateRequestBody,
} from 'hems/firmwareUpdate';

/** axios가 인코딩을 자동으로 하지 않고 주는 그대로의 parameter를 사용하도록 설정하여 double 인코딩 방지 */
const customParamSerializer = (params: any) => {
  return new URLSearchParams(params).toString();
};
export default class FirmwareUpdateService extends Service {
  constructor(axios: AxiosInstance) {
    super(axios);
  }

  /** 펌웨어 업데이트 Group List */
  getFirmwareUpdateGroupList = (params: FirmwareUpdateListParams): Promise<FirmwareUpdateGroupResponse> => {
    return this.get<FirmwareUpdateGroupResponse>(apiWrapper.managerApiV1('/firmwares/update-groups'), params);
  };

  /** 펌웨어 업데이트 Device Info List */
  getFirmwareUpdateDeviceInfoList = (
    updateGroupId: string,
    params: CommonListQueryParamNew
  ): Promise<FirmwareUpdateDeviceResultResponse> => {
    const encodedUpdateGroupId = encodeURIComponent(updateGroupId);

    return this.get<FirmwareUpdateDeviceResultResponse>(
      apiWrapper.managerApiV1(`/firmwares/update-groups/${encodedUpdateGroupId}/devices`),
      params,
      {
        paramsSerializer: customParamSerializer,
      }
    );
  };

  /** 펌웨어 업데이트 Group Name 중복 체크 */
  isValidGroupNameDuplicate = (
    params: FirmwareUpdateValidateGroupNameParams
  ): Promise<FirmwareUpdateValidateResponse> => {
    return this.post<FirmwareUpdateValidateResponse>(
      apiWrapper.managerApiV1('/firmwares/update-groups/id/validate'),
      params
    );
  };

  /** 펌웨어 업데이트 Auto update 일때, configuration 중복 체크 */
  isValidAutoUpdateConfigurationDuplicate = (
    params: FirmwareUpdateValidateConfigurationParams
  ): Promise<FirmwareUpdateValidateResponse> => {
    return this.post<FirmwareUpdateValidateResponse>(
      apiWrapper.managerApiV1('/firmwares/update-groups/update-type/validate'),
      params
    );
  };

  /** 펌웨어 업데이트 Create > Step2 Device List */
  getFirmwareUpdateCreateStep2DeviceList = (
    params: FirmwareUpdateListParams
  ): Promise<FirmwareUpdateCreateStep2DeviceResponse> => {
    return this.get<FirmwareUpdateCreateStep2DeviceResponse>(apiWrapper.managerApiV1('/firmwares/devices'), params);
  };

  /** 펌웨어 정보 생성 */
  createFirmwareInfo = (firmwareInfo: CreateFirmwareRequestBody): Promise<EmptyResponse> => {
    return this.post<EmptyResponse>(apiWrapper.managerApiV1('/firmwares'), firmwareInfo);
  };

  /** 펌웨어 업데이트 File Presigned URL 정보 */
  getFirmwareUpdateFilePresignedURL = (
    firmwareId: string,
    presignedInfo: GetFirmwarePresignedURLParams
  ): Promise<FirmwareUpdatePresignedURLResponse> => {
    return this.get<FirmwareUpdatePresignedURLResponse>(
      apiWrapper.managerApiV1(`/firmwares/${firmwareId}/storage`),
      presignedInfo
    );
  };

  /** 펌웨어 파일 S3에 등록 */
  uploadFirmwareUpdateFile = (fileURL: string, file: File): Promise<Response> => {
    return fetch(fileURL, {
      method: 'PUT',
      body: file,
      headers: {
        'x-ms-blob-type': 'BlockBlob',
      },
    });
  };

  /** 펌웨어 파일 S3에서 다운로드 */
  downloadFirmwareUpdateFile = (fileURL: string): Promise<Response> => {
    return fetch(fileURL, {
      method: 'GET',
    });
  };

  /** 펌웨어 업데이트 Set 생성 */
  createFirmwareUpdateSet = (firmwareSetInfo: CreateFirmwareSetRequestBody): Promise<FirmwareUpdatePostAPIResponse> => {
    return this.post<FirmwareUpdatePostAPIResponse>(apiWrapper.managerApiV1('/firmware-sets'), firmwareSetInfo);
  };

  /** 펌웨어 업데이트 그룹 정보 생성 */
  createFirmwareUpdateGroupInfo = (
    firmwareGroupInfo: CreateFirmwareGroupRequestBody
  ): Promise<FirmwareUpdatePostAPIResponse> => {
    return this.post<FirmwareUpdatePostAPIResponse>(
      apiWrapper.managerApiV1('/firmwares/update-groups'),
      firmwareGroupInfo
    );
  };

  /** 펌웨어 업데이트 Manual 일때, 펌웨어 등록 및 siteId 등록 */
  pushManualFirmwareUpdate = (
    updateGroupId: string,
    firmwareInfo: PushFirmwareUpdateRequestBody
  ): Promise<FirmwareUpdatePostAPIResponse> => {
    const encodedUpdateGroupId = encodeURIComponent(updateGroupId);

    return this.post<FirmwareUpdatePostAPIResponse>(
      apiWrapper.managerApiV1(`/firmwares/update-groups/${encodedUpdateGroupId}/update`),
      firmwareInfo,
      {
        paramsSerializer: customParamSerializer,
      }
    );
  };

  /** 펌웨어 업데이트 Auto Update 일때, 펌웨어 등록 및 Exclude siteId 등록 */
  pushAutoFirmwareUpdate = (
    updateGroupId: string,
    excludeSiteIdInfo: pushFirmwareUpdateExcludeSiteIdsRequestBody
  ): Promise<FirmwareUpdatePostAPIResponse> => {
    const encodedUpdateGroupId = encodeURIComponent(updateGroupId);

    return this.post<FirmwareUpdatePostAPIResponse>(
      apiWrapper.managerApiV1(`/firmwares/update-groups/${encodedUpdateGroupId}/excluded-sites`),
      excludeSiteIdInfo,
      {
        paramsSerializer: customParamSerializer,
      }
    );
  };

  /** 펌웨어 release File List */
  getFirmwareReleaseList = async (): Promise<FirmwareReleaseDataResponse> => {
    return await this.get<FirmwareReleaseDataResponse>(apiWrapper.managerApiV1('/firmwares/releases'));
  };

  /** 펌웨 업데이트 Auto Update 정보 삭제 (Auto Update의 경우에만 삭제 할 수 있고 Manual은 삭제 불가능) */
  deleteFirmwareAutoUpdateInfo = (updateGroupId: string): Promise<EmptyObject> => {
    const encodedUpdateGroupId = encodeURIComponent(updateGroupId);

    return this.delete<EmptyObject>(
      apiWrapper.managerApiV1(`/firmwares/update-groups/${encodedUpdateGroupId}`),
      undefined,
      {
        paramsSerializer: customParamSerializer,
      }
    );
  };
}
